import { z } from 'zod';
/**
 * ehr#730 (Jay 10-30, SHARE_ACCESS_10-30-V11): who can open a contact and what they can do on each
 * of its tabs. One tab list (CONTACT_TABS, V11's TAB_REGISTRY) drives the drawer's tab strip, the
 * Share window's "Access per tab" table and the server's tab check, so a new tab shows up in all
 * three at once.
 *
 * A grant gives one person (or everyone in a role, minus the people unticked) a level per tab
 * (Hidden, View or Edit) plus three record permissions (Share with others, Reassign, Archive).
 * Someone who isn't the owner, an admin or a manager can only grant up to their own access.
 *
 * Grants saved before V11 (the old two-step Share Contact window, and the View share a shared note
 * or form gives its client, ehr#656) carry no per-tab levels: they keep today's behaviour (every
 * tab, no limits) until someone changes them in Who has access.
 */
export const ACCESS_LEVELS = ['hidden', 'view', 'edit'];
export const ACCESS_LEVEL_LABEL = {
    hidden: 'Hidden',
    view: 'View',
    edit: 'Edit',
};
export const RECORD_RIGHT_KEYS = ['share', 'reassign', 'archive'];
export const RECORD_RIGHT_LABEL = {
    share: 'Share with others',
    reassign: 'Reassign',
    archive: 'Archive',
};
export const NO_RIGHTS = { share: false, reassign: false, archive: false };
export const ALL_RIGHTS = { share: true, reassign: true, archive: true };
/** V11's denial line, word for word (tooltips, the server's 403s). */
export const ACCESS_DENIED_MESSAGE = 'You do not have permissions to perform this action. Please contact your admin.';
/** A sharer who isn't the owner (or an admin / manager) asked for more than they hold. */
export const ACCESS_CAP_MESSAGE = 'You can grant up to your own access';
export const CONTACT_TAB_KEYS = [
    'summary',
    'demographics',
    'followup',
    'appointments',
    'clinical_notes',
    'notes',
    'forms',
    'tasks',
    'email',
    'messages',
    'files',
    'licensure',
    'eligibility',
    'authorizations',
];
const ALL = ['client', 'referral_source', 'staff'];
/**
 * The drawer's tab strip, in its order (ContactDrawer reads this list). Mockup order
 * (EHR-7-28-V24): Messages sits between Email and Files. ehr#230: Summary leads the client drawer.
 * ehr#743 (V11): the Demographics tab reads "Profile"; its key stays `demographics` so
 * ?view=demographics links keep working. #138: Progress notes and Treatment plans live together
 * under Clinical Notes (Jay 10-08). #132: Contact Notes. ehr#640: Screeners are a filter in Forms.
 * alertshub#12 (Jay 10-13): Licensure on staff contacts only.
 */
export const CONTACT_TABS = [
    { key: 'summary', label: 'Summary', categories: ['client'] },
    {
        key: 'demographics',
        label: 'Profile',
        otherLabel: 'Contact Info',
        categories: ['client'],
        accessCategories: ALL,
        lockMin: 'view',
    },
    { key: 'followup', label: 'Follow-up', categories: ALL },
    { key: 'appointments', label: 'Appointments', categories: ALL },
    { key: 'clinical_notes', label: 'Clinical Notes', categories: ALL },
    { key: 'notes', label: 'Contact Notes', categories: ALL },
    { key: 'forms', label: 'Forms', categories: ['client'] },
    { key: 'tasks', label: 'Tasks', categories: ALL },
    { key: 'email', label: 'Email', categories: ALL },
    { key: 'messages', label: 'Messages', categories: ALL, lockMax: 'view', external: true },
    { key: 'files', label: 'Files', categories: ALL },
    { key: 'licensure', label: 'Licensure', categories: ['staff'] },
    { key: 'eligibility', label: 'Eligibility', categories: ['client'] },
    { key: 'authorizations', label: 'Authorizations', categories: ['client'] },
];
export function contactTab(key) {
    return CONTACT_TABS.find((t) => t.key === key);
}
/** The tabs the drawer strip shows for this kind of contact. */
export function drawerTabs(category) {
    return CONTACT_TABS.filter((t) => t.categories.includes(category));
}
/** The rows of the Access per tab table for this kind of contact. */
export function accessTabs(category) {
    return CONTACT_TABS.filter((t) => (t.accessCategories ?? t.categories).includes(category));
}
/** The rows common to several contacts (sharing a mix of clients and staff at once). */
export function accessTabsFor(categories) {
    if (!categories.length)
        return [];
    return CONTACT_TABS.filter((t) => categories.every((c) => (t.accessCategories ?? t.categories).includes(c)));
}
export function tabLabel(t, category) {
    return category && category !== 'client' && t.otherLabel ? t.otherLabel : t.label;
}
export function levelRank(l) {
    return ACCESS_LEVELS.indexOf(l ?? 'hidden');
}
/** The higher of two levels. */
export function maxLevel(a, b) {
    return levelRank(a) >= levelRank(b) ? (a ?? 'hidden') : (b ?? 'hidden');
}
/** The lower of two levels. */
export function minLevel(a, b) {
    return levelRank(a) <= levelRank(b) ? (a ?? 'hidden') : (b ?? 'hidden');
}
/** A level kept inside the tab's lockMin / lockMax. */
export function clampLevel(t, level) {
    let l = level;
    if (t.lockMin && levelRank(l) < levelRank(t.lockMin))
        l = t.lockMin;
    if (t.lockMax && levelRank(l) > levelRank(t.lockMax))
        l = t.lockMax;
    return l;
}
/** The highest level a tab takes (Edit, or View for Messages). */
export function topLevel(t) {
    return t.lockMax ?? 'edit';
}
/** Every tab at its top level: the owner, admins and managers. */
export function fullTabs(category) {
    const out = {};
    for (const t of accessTabs(category))
        out[t.key] = topLevel(t);
    return out;
}
/** Every tab at one level, kept inside each tab's locks. */
export function tabsAt(category, level) {
    const out = {};
    for (const t of accessTabs(category))
        out[t.key] = clampLevel(t, level);
    return out;
}
/**
 * What a person holds on a contact through all the grants that reach them (their own grant and
 * their role's): the highest level per tab and every record permission any grant gives. A tab a
 * grant leaves out is Hidden (V11 grantAccessShape), then the tab's locks apply (the Profile is
 * never below View).
 */
export function mergeGrants(category, grants) {
    const tabs = {};
    const rights = { ...NO_RIGHTS };
    const unrestricted = grants.some((g) => g.tabs == null);
    for (const t of accessTabs(category)) {
        let lvl = 'hidden';
        if (unrestricted)
            lvl = topLevel(t);
        else
            for (const g of grants)
                lvl = maxLevel(lvl, g.tabs?.[t.key] ?? 'hidden');
        tabs[t.key] = clampLevel(t, lvl);
    }
    for (const g of grants)
        for (const k of RECORD_RIGHT_KEYS)
            if (g.rights?.[k])
                rights[k] = true;
    return { tabs, rights, unrestricted };
}
/** A grant's levels for display: tabs left out read Hidden, locks applied. */
export function grantTabs(category, tabs) {
    return mergeGrants(category, [{ tabs }]).tabs;
}
export function rightsOf(r) {
    return { share: !!r?.share, reassign: !!r?.reassign, archive: !!r?.archive };
}
const plural = (n, one) => `${n} ${one}${n === 1 ? '' : 's'}`;
/**
 * V11 accessSummary: "Full Access" (Edit everywhere + all three permissions), "Edit on all tabs",
 * else "Edit on 3 tabs · View on 2 tabs" ("No tabs" when everything is hidden). A grant from before
 * per-tab access reads so.
 */
export function accessSummary(category, tabs, rights) {
    if (tabs == null)
        return 'Every tab, no limits (shared before per-tab access)';
    const list = accessTabs(category);
    const atTop = list.every((t) => tabs[t.key] === topLevel(t));
    if (atTop)
        return RECORD_RIGHT_KEYS.every((k) => rights?.[k]) ? 'Full Access' : 'Edit on all tabs';
    const edit = list.filter((t) => tabs[t.key] === 'edit').length;
    const view = list.filter((t) => tabs[t.key] === 'view').length;
    const parts = [];
    if (edit)
        parts.push(`Edit on ${plural(edit, 'tab')}`);
    if (view)
        parts.push(`View on ${plural(view, 'tab')}`);
    return parts.length ? parts.join(' · ') : 'No tabs';
}
/** "Share with others, Reassign" or "None". */
export function rightsSummary(rights) {
    const on = RECORD_RIGHT_KEYS.filter((k) => rights?.[k]).map((k) => RECORD_RIGHT_LABEL[k]);
    return on.length ? on.join(', ') : 'None';
}
/** The "Previous owner keeps" choices on Reassign (V11 default Nothing, decision 3a). */
export const REASSIGN_KEEP = ['none', 'view', 'edit', 'full'];
export const REASSIGN_KEEP_LABEL = {
    none: 'Nothing',
    view: 'View',
    edit: 'Edit',
    full: 'Full Access',
};
/** The grant the previous owner keeps after a reassign; null for Nothing. */
export function keepGrant(category, keep) {
    if (keep === 'none')
        return null;
    return {
        tabs: tabsAt(category, keep === 'view' ? 'view' : 'edit'),
        rights: keep === 'full' ? { ...ALL_RIGHTS } : { ...NO_RIGHTS },
    };
}
/**
 * Over the sharer's own access? Returns the tabs and permissions asked for beyond `cap` (empty when
 * within it). `cap` null = the owner, an admin or a manager: no tab limit.
 */
export function overCap(asked, cap) {
    const tabs = cap.tabs
        ? Object.entries(asked.tabs)
            .filter(([k, l]) => levelRank(l) > levelRank(cap.tabs?.[k] ?? 'hidden'))
            .map(([k]) => k)
        : [];
    const rights = RECORD_RIGHT_KEYS.filter((k) => asked.rights[k] && !cap.rights[k]);
    return { tabs, rights };
}
/**
 * Roles a contact can be shared with. Admins and managers already open every contact, so a grant
 * to them would change nothing.
 */
export const SHAREABLE_ROLES = [
    'clinician',
    'supervisor',
    'front_desk',
];
export const ROLE_LABEL = {
    admin: 'Admin',
    manager: 'Manager',
    clinician: 'Clinician',
    supervisor: 'Supervisor',
    front_desk: 'Front Desk',
};
/** Roles that open every contact in the practice (contacts visibilityWhere). */
export function seesEveryContact(role) {
    return role === 'admin' || role === 'manager';
}
const uuid = z.string().uuid();
export const tabLevelsSchema = z
    .record(z.enum(CONTACT_TAB_KEYS), z.enum(ACCESS_LEVELS))
    .refine((r) => Object.keys(r).length > 0, 'Set access for at least one tab');
export const recordRightsSchema = z.object({
    share: z.boolean().default(false),
    reassign: z.boolean().default(false),
    archive: z.boolean().default(false),
});
/**
 * POST /contacts/access/share: give these people and roles this access on these contacts (one
 * contact from the chart, several from the Contacts list's checkboxes). An existing grant to the
 * same person or role is replaced (Change in Who has access sends `change: true`).
 */
export const contactAccessShareSchema = z
    .object({
    contactIds: z.array(uuid).min(1).max(200),
    users: z.array(uuid).max(200).default([]),
    roles: z
        .array(z.object({
        role: z.enum(SHAREABLE_ROLES),
        excluded: z.array(uuid).max(500).default([]),
    }))
        .max(SHAREABLE_ROLES.length)
        .default([]),
    tabs: tabLevelsSchema,
    rights: recordRightsSchema.default({}),
    notify: z.boolean().default(true),
    change: z.boolean().default(false),
})
    .refine((b) => b.users.length + b.roles.length > 0, {
    message: 'Pick at least one user or role',
    path: ['users'],
});
/** POST /contacts/access/reassign: a new owner for these contacts; what the old owner keeps. */
export const contactAccessReassignSchema = z.object({
    contactIds: z.array(uuid).min(1).max(200),
    ownerUserId: uuid,
    keep: z.enum(REASSIGN_KEEP).default('none'),
});
//# sourceMappingURL=contactAccess.js.map